import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, CheckCircle2, FileCheck2, Lock, Truck, Battery, Package, Send, ArrowRight, Award, Building2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from '../../hooks/use-toast';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGENCY_TYPES = [
  { v: 'state_agency', l: 'State Agency' },
  { v: 'county', l: 'County' },
  { v: 'city', l: 'City' },
  { v: 'school_district', l: 'School District' },
  { v: 'university', l: 'University' },
  { v: 'public_entity', l: 'Other Public Entity' },
];

const GovLanding = () => {
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    agency_name: '', agency_type: 'state_agency',
    org_address: '', org_city: '', org_state: '', org_zip: '',
    email_domain: '',
    contact_name: '', contact_email: '', contact_phone: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestRef, setRequestRef] = useState('');

  useEffect(() => {
    document.title = 'USKE Procurement Portal';
    axios.get(`${API}/gov/public/contract-summary`).then(r => setSummary(r.data)).catch(() => setSummary({}));
  }, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.agency_name || !form.contact_name || !form.contact_email) {
      toast({ title: 'Please fill in required fields', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/gov/public/access-request`, form);
      setRequestRef(res.data?.request_ref || '');
      toast({ title: 'Request submitted', description: 'Our team will reach out within 1–2 business days.' });
      setSubmitted(true);
    } catch (err) {
      toast({ title: 'Submission failed', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="gov-landing-page">
      {/* Top sticky bar */}
      <div className="bg-blue-950 text-blue-100 text-xs py-1.5 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> USKE Procurement Portal · Washington State DES Contract 09925</div>
        <span className="text-blue-300">Powered by USKE SUPPLY LLC · Contract 09925</span>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 text-white py-14 sm:py-24 px-4">
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-700/40 border border-blue-400/30 text-blue-100 px-3 py-1 rounded-full text-xs font-medium mb-4">
              <Award className="h-3.5 w-3.5" /> Awarded Vendor · DES Master Contract 09925
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-2">
              The <span className="text-blue-300">USKE Procurement Portal</span>
            </h1>
            <div className="text-sm sm:text-base text-blue-200 font-medium mb-4">
              Procurement &amp; Financial Management Platform
            </div>
            <p className="text-base sm:text-lg text-blue-100 mb-6">
              State agencies, counties, cities, school districts, universities, nonprofits and authorized commercial purchasers can place orders,
              track deliveries, and manage the entire order-to-cash cycle at contract-locked pricing through USKE Supply LLC.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={portalPath('/login')}><Button className="bg-white text-blue-900 hover:bg-blue-50" data-testid="hero-signin-btn">Sign in to portal <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
              <a href="#enroll"><Button variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="hero-enroll-btn">Enroll my agency</Button></a>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 text-center">
              <div><div className="text-3xl font-bold">{summary?.stats?.product_count ?? '—'}</div><div className="text-xs text-blue-200">Contract SKUs</div></div>
              <div><div className="text-3xl font-bold">1.5%</div><div className="text-xs text-blue-200">DES Mgmt Fee</div></div>
              <div><div className="text-3xl font-bold">Net 30</div><div className="text-xs text-blue-200">Payment terms</div></div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <div className="text-sm text-blue-200 mb-2">Contract Snapshot</div>
              <div className="text-2xl font-bold mb-3">Office Supplies — DES 09925</div>
              <ul className="space-y-2 text-sm text-blue-100">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Statewide pricing locked to awarded rates</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Multi-level agency approval workflows</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Quarterly DES sales reporting auto-generated</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Two-factor authentication for all users</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> PO, invoice and packing slip PDFs included</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">Awarded Categories</h2>
            <p className="text-slate-500 text-sm mt-1">All items are sourced and shipped under DES Contract 09925.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <Card className="border-2 hover:shadow-lg transition" data-testid="category-card-batteries">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><Battery className="h-6 w-6" /></div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Category 3</div>
                  <div className="text-lg font-bold text-slate-800">Batteries</div>
                  <p className="text-sm text-slate-500 mt-1">Duracell & Eneloop AA, AAA, C, D, 9V and rechargeable batteries — {summary?.category_counts?.batteries || 0} SKUs.</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 hover:shadow-lg transition" data-testid="category-card-mailing">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Package className="h-6 w-6" /></div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Category 14</div>
                  <div className="text-lg font-bold text-slate-800">Mailing, Packing & Shipping</div>
                  <p className="text-sm text-slate-500 mt-1">Bubble mailers, shipping boxes, packing tape, labels, envelopes — {summary?.category_counts?.['mailing-packing-shipping'] || 0} SKUs.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sample products walkthrough */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">A look at the catalog</h2>
              <p className="text-slate-500 text-sm">Authorized users see the full catalog and place orders directly from the portal.</p>
            </div>
            <Link to={portalPath('/login')}><Button variant="outline" className="border-blue-700 text-blue-700 hover:bg-blue-50">Sign in to browse</Button></Link>
          </div>
          {!summary ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {(summary.sample_products || []).map(p => (
                <Card key={p.id} className="border-2" data-testid={`landing-product-${p.id}`}>
                  <CardContent className="p-3">
                    <div className="aspect-square bg-slate-100 rounded mb-2 overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="font-semibold text-slate-800 text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.manufacturer}</div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <Link to={portalPath('/login')} className="text-[11px] text-blue-700 underline" data-testid={`landing-price-locked-${p.id}`}>🔒 Sign in for price</Link>
                      <span className="text-[10px] uppercase text-emerald-700 font-semibold">Contract</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="container mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 text-center mb-8">How procurement works in the portal</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Building2, title: 'Enroll your agency', desc: 'Submit the form below. We provision your admin account and approval workflow.' },
              { icon: Lock, title: 'Secure sign-in + MFA', desc: 'Government-grade JWT auth with TOTP two-factor for every user.' },
              { icon: FileCheck2, title: 'Approve & Track', desc: 'One- or two-level approvals, audit trail, PO / invoice / packing slip PDFs.' },
              { icon: Truck, title: 'Ship & report', desc: 'USKE fulfills, tracking numbers auto-notify, and quarterly DES reports are generated.' },
            ].map((s, i) => (
              <Card key={i} className="border-2">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center mb-3"><s.icon className="h-5 w-5" /></div>
                  <div className="font-semibold text-slate-800">{s.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enroll form */}
      <section id="enroll" className="py-14 px-4 bg-blue-950 text-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold">Request access for your organisation</h2>
            <p className="text-blue-200 text-sm mt-1">Our team will reach out within 1–2 business days to provision your accounts.</p>
            <div className="mt-3 flex justify-center gap-2 flex-wrap">
              <Link to={portalPath('/help/agency-registration')} className="text-sm text-blue-200 hover:text-white underline" data-testid="landing-agency-guide-link">📘 Read the Registration Guide</Link>
              <span className="text-blue-400">·</span>
              <Link to={portalPath('/help/punchout')} className="text-sm text-blue-200 hover:text-white underline" data-testid="landing-punchout-guide-link">🔌 Punchout Integration Guide</Link>
            </div>
          </div>

          {submitted ? (
            <Card className="border-2 border-emerald-400 bg-white text-slate-800">
              <CardContent className="p-8 text-center" data-testid="enroll-success">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-lg font-bold">Thank you — we&apos;ve received your request.</h3>
                {requestRef && (
                  <div className="inline-block mt-3 bg-blue-50 border border-blue-200 rounded px-4 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reference</div>
                    <div className="font-mono text-lg text-blue-900" data-testid="request-ref">{requestRef}</div>
                  </div>
                )}
                <p className="text-slate-500 mt-3">A confirmation email has been sent to <b>{form.contact_email}</b>. Our team will follow up shortly.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white text-slate-800">
              <CardContent className="p-6 sm:p-8">
                <form className="grid grid-cols-1 sm:grid-cols-6 gap-4" onSubmit={submitRequest}>
                  {/* --- Organisation --- */}
                  <div className="sm:col-span-6">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Organisation</div>
                  </div>
                  <div className="sm:col-span-4">
                    <Label>Agency name *</Label>
                    <Input value={form.agency_name} onChange={e => setForm({ ...form, agency_name: e.target.value })} required data-testid="enroll-agency-name" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Agency type *</Label>
                    <Select value={form.agency_type} onValueChange={(v) => setForm({ ...form, agency_type: v })}>
                      <SelectTrigger data-testid="enroll-agency-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{AGENCY_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-6">
                    <Label>Street address</Label>
                    <Input value={form.org_address} onChange={e => setForm({ ...form, org_address: e.target.value })} placeholder="123 State Capitol Way" data-testid="enroll-org-address" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>City</Label>
                    <Input value={form.org_city} onChange={e => setForm({ ...form, org_city: e.target.value })} placeholder="Olympia" data-testid="enroll-org-city" />
                  </div>
                  <div className="sm:col-span-1">
                    <Label>State</Label>
                    <Input value={form.org_state} onChange={e => setForm({ ...form, org_state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="WA" maxLength={2} data-testid="enroll-org-state" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>ZIP code</Label>
                    <Input value={form.org_zip} onChange={e => setForm({ ...form, org_zip: e.target.value })} placeholder="98501" data-testid="enroll-org-zip" />
                  </div>

                  {/* --- Agency Administrator --- */}
                  <div className="sm:col-span-6 mt-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Agency Administrator</div>
                    <p className="text-[11px] text-slate-500 -mt-1 mb-1">The person below will be set up as your agency&apos;s first administrator and will be able to invite additional users.</p>
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Administrator name *</Label>
                    <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} required data-testid="enroll-contact-name" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Administrator email *</Label>
                    <Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} required data-testid="enroll-contact-email" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Administrator phone</Label>
                    <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="(206) 555-0100" data-testid="enroll-contact-phone" />
                  </div>
                  <div className="sm:col-span-3">
                    <Label>Preferred agency email domain</Label>
                    <Input value={form.email_domain} onChange={e => setForm({ ...form, email_domain: e.target.value })} placeholder="commerce.wa.gov" data-testid="enroll-email-domain" />
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      We strongly recommend an <strong>official organisational email address</strong>
                      (e.g. <code className="text-[10px] bg-slate-100 px-1 rounded">john.smith@commerce.wa.gov</code>).
                      Personal domains such as <em>gmail.com</em> may be approved on a case-by-case basis.
                    </p>
                  </div>

                  {/* --- Notes --- */}
                  <div className="sm:col-span-6">
                    <Label>Notes (special requirements, expected categories, etc.)</Label>
                    <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="enroll-notes" />
                  </div>
                  <div className="sm:col-span-6">
                    <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white w-full sm:w-auto" data-testid="enroll-submit">
                      <Send className="h-4 w-4 mr-1" /> {submitting ? 'Sending…' : 'Submit access request'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 text-xs py-4 px-4 text-center">
        USKE SUPPLY LLC · Washington State DES Contract 09925 · {new Date().getFullYear()} ·
        <a href="mailto:info@uskesupplyllc.com" className="ml-1 underline hover:text-white">info@uskesupplyllc.com</a>
      </footer>
    </div>
  );
};

export default GovLanding;
